import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

// AG-Grid
import AgGrid from '../../shared/agGrid/agGrid.component';
import useGridApi from '../../shared/hooks/useGridApi.hook';

// components
import Button from '../../shared/button.component';

// other imports
import { UserExpirationWithId } from '@queries/users';
import { defaultColumDeinitions, getColumDefinitions } from '../../shared/columnDefinitions/userExpirationGrid';
import { userSelector } from '../../shared/store/selectors';

type UserExpirationGridProps = {
  canModify?: boolean,
  users: UserExpirationWithId[],
  selectedUsers: number[],
  editedUsers: number[],
  isLoading: boolean,
  handleSelect: (selectedIds: number[]) => void,
  onClickNewDate: () => void,
  children?: React.ReactNode,
}

const UserExpirationGrid = ({
  canModify = false,
  users,
  selectedUsers,
  editedUsers,
  isLoading,
  handleSelect,
  onClickNewDate,
  children,
}: UserExpirationGridProps) => {
  const user = useSelector(userSelector);
  const { gridApi, onGridReady } = useGridApi();

  const onSelect = useCallback((selectedId: number) => {
    const newValues = selectedUsers.includes(selectedId)
      ? selectedUsers.filter(userId => userId !== selectedId) : [...selectedUsers, selectedId];
    handleSelect([...newValues]);
  }, [selectedUsers, handleSelect]);

  const isDisabled = useCallback((userId: number) => {
    const findUser = users.find(user => user.id === userId);
    return findUser?.sso === user?.sso;
  }, [user, users]);

  const unselectAllUsers = useCallback(() => {
    handleSelect([]);
  }, [handleSelect]);

  const columnDefinitions = canModify
    ? getColumDefinitions({ handleSelect: onSelect, selectedUsers, isDisabled, editedUsers })
    : defaultColumDeinitions;

  useEffect(() => {
    if (gridApi) {
      gridApi?.redrawRows();
    }
  }, [selectedUsers, gridApi]);

  return (
    <div className="page-content-container">
      <AgGrid
        columnDefs={columnDefinitions}
        headerHeight={50}
        rowData={users}
        isGridLoading={isLoading}
        onGridReady={onGridReady}
        withSearchBar
      />
      {canModify && (
        <div className="row no-gutters">
          <div className="col">
            <Button
              onClick={unselectAllUsers}
              kind="secondary"
              className="a-mx-10"
              disabled={isLoading || selectedUsers.length === 0}
            >
              Clear all
            </Button>
            <Button
              type="button"
              kind="secondary"
              disabled={selectedUsers.length === 0}
              onClick={onClickNewDate}
            >
              New Date for Selected
            </Button>
          </div>
          {children}
        </div>
      )}
    </div>
  );
};

export default UserExpirationGrid;
